import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import MontureCard from "./MontureCard";
import { useMontures } from "@/context/MonturesContext";

const DRAG_CLICK_THRESHOLD = 6;
// Triple les items pour un défilement infini sans saut visible.
// On garde toujours scrollLeft à l'intérieur du tiers du milieu et on
// "rentre" silencieusement avant que l'utilisateur n'atteigne un bord.
const REPEATS = 3;
const PIXELS_PER_SECOND = 18;

export default function MonturesDuMoment({ onSelect }) {
    const { duMoment } = useMontures();
    const scrollRef = useRef(null);
    const rafRef = useRef(null);
    const lastTsRef = useRef(0);
    const accRef = useRef(0);
    const smoothingRef = useRef(false); // pendant scrollBy smooth, pas de normalize
    const [paused, setPaused] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const dragState = useRef({
        down: false,
        startX: 0,
        startScroll: 0,
        moved: 0,
    });

    // Normalise scrollLeft pour rester au milieu (boucle invisible)
    const normalize = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        if (smoothingRef.current) return; // pas de saut pendant un smooth scroll
        const block = el.scrollWidth / REPEATS;
        if (!block) return;
        if (el.scrollLeft >= block * 2) {
            el.scrollLeft -= block;
            dragState.current.startScroll -= block;
        } else if (el.scrollLeft < block * 0.5) {
            el.scrollLeft += block;
            dragState.current.startScroll += block;
        }
    }, []);

    // Place le scroll au milieu au montage et quand la liste change
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el || duMoment.length === 0) return;
        // Attendre le layout pour que scrollWidth soit correct
        requestAnimationFrame(() => {
            if (!el) return;
            el.scrollLeft = el.scrollWidth / REPEATS;
        });
    }, [duMoment.length]);

    const tick = useCallback(
        (ts) => {
            const el = scrollRef.current;
            if (!el) return;
            if (!lastTsRef.current) lastTsRef.current = ts;
            const dt = (ts - lastTsRef.current) / 1000;
            lastTsRef.current = ts;
            accRef.current += dt * PIXELS_PER_SECOND;
            const step = Math.floor(accRef.current);
            if (step >= 1) {
                accRef.current -= step;
                el.scrollLeft += step;
                normalize();
            }
            rafRef.current = requestAnimationFrame(tick);
        },
        [normalize]
    );

    useEffect(() => {
        if (paused || isDragging) {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            return undefined;
        }
        lastTsRef.current = 0;
        accRef.current = 0;
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [paused, isDragging, tick]);

    const scrollByCard = useCallback(
        (dir) => {
            const el = scrollRef.current;
            if (!el) return;
            // Normalise AVANT scrollBy pour rester dans le tiers du milieu.
            normalize();
            const card = el.querySelector("[data-monture-card]");
            const width = card ? card.offsetWidth + 20 : 320;
            smoothingRef.current = true;
            el.scrollBy({ left: dir * width, behavior: "smooth" });
            // Désactive le verrou après la fin de l'animation smooth
            setTimeout(() => {
                smoothingRef.current = false;
                normalize();
            }, 500);
        },
        [normalize]
    );

    const onMouseDown = useCallback((e) => {
        const el = scrollRef.current;
        if (!el) return;
        dragState.current.down = true;
        dragState.current.startX = e.pageX - el.offsetLeft;
        dragState.current.startScroll = el.scrollLeft;
        dragState.current.moved = 0;
        setIsDragging(true);
    }, []);

    const endDrag = useCallback(() => {
        if (!dragState.current.down) return;
        dragState.current.down = false;
        setIsDragging(false);
    }, []);

    const onMouseMove = useCallback(
        (e) => {
            if (!dragState.current.down) return;
            const el = scrollRef.current;
            if (!el) return;
            const x = e.pageX - el.offsetLeft;
            const walk = (x - dragState.current.startX) * 1.5;
            dragState.current.moved = Math.abs(walk);
            if (dragState.current.moved > 3) e.preventDefault();
            el.scrollLeft = dragState.current.startScroll - walk;
            normalize();
        },
        [normalize]
    );

    const onClickCapture = useCallback((e) => {
        if (dragState.current.moved > DRAG_CLICK_THRESHOLD) {
            e.preventDefault();
            e.stopPropagation();
        }
    }, []);

    // Touch support pour mobile (drag tactile fluide)
    const onTouchStart = useCallback((e) => {
        const el = scrollRef.current;
        if (!el) return;
        const t = e.touches[0];
        dragState.current.down = true;
        dragState.current.startX = t.pageX - el.offsetLeft;
        dragState.current.startScroll = el.scrollLeft;
        dragState.current.moved = 0;
        setIsDragging(true);
    }, []);

    const onTouchMove = useCallback(
        (e) => {
            if (!dragState.current.down) return;
            const el = scrollRef.current;
            if (!el) return;
            const t = e.touches[0];
            const x = t.pageX - el.offsetLeft;
            const walk = (x - dragState.current.startX) * 1.5;
            dragState.current.moved = Math.abs(walk);
            el.scrollLeft = dragState.current.startScroll - walk;
            normalize();
        },
        [normalize]
    );

    // Surveille aussi les scrolls clavier / molette (mais pas pendant
    // un drag ou pendant une animation smooth déclenchée par les flèches).
    const onScroll = useCallback(() => {
        if (dragState.current.down) return;
        // throttle léger : normalise uniquement si on est très loin
        const el = scrollRef.current;
        if (!el) return;
        const block = el.scrollWidth / REPEATS;
        if (
            el.scrollLeft >= block * 2.5 ||
            el.scrollLeft < block * 0.25
        ) {
            normalize();
        }
    }, [normalize]);

    if (!duMoment || duMoment.length === 0) return null;
    const repeated = Array.from({ length: REPEATS }).flatMap(() => duMoment);

    return (
        <section
            id="montures-du-moment"
            className="relative py-24 md:py-32 bg-lpl-bg-alt overflow-hidden"
            data-testid="montures-du-moment-section"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
                    <div>
                        <p className="font-serif italic text-2xl md:text-3xl text-lpl-terracotta mb-3">
                            Nos coups de projecteur
                        </p>
                        <h2
                            className="font-display font-semibold text-5xl md:text-7xl text-lpl-text leading-[0.95] tracking-tight"
                            data-testid="montures-du-moment-title"
                        >
                            Nos montures{" "}
                            <span className="font-serif italic font-normal text-lpl-primary-deep">
                                du moment
                            </span>
                            <span className="font-serif italic font-normal text-lpl-lavender">
                                .
                            </span>
                        </h2>
                    </div>

                    <div
                        className="flex items-center gap-3"
                        data-testid="montures-controls"
                    >
                        <button
                            type="button"
                            onClick={() => scrollByCard(-1)}
                            aria-label="Précédent"
                            data-testid="control-prev"
                            className="w-12 h-12 rounded-full border-2 border-lpl-text text-lpl-text hover:bg-lpl-text hover:text-lpl-bg transition-colors flex items-center justify-center"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button
                            type="button"
                            onClick={() => setPaused((p) => !p)}
                            aria-label={paused ? "Reprendre" : "Pause"}
                            data-testid="control-playpause"
                            className="w-12 h-12 rounded-full bg-lpl-primary text-lpl-text hover:bg-lpl-text hover:text-lpl-bg transition-colors flex items-center justify-center"
                        >
                            {paused ? <Play size={18} /> : <Pause size={18} />}
                        </button>
                        <button
                            type="button"
                            onClick={() => scrollByCard(1)}
                            aria-label="Suivant"
                            data-testid="control-next"
                            className="w-12 h-12 rounded-full border-2 border-lpl-text text-lpl-text hover:bg-lpl-text hover:text-lpl-bg transition-colors flex items-center justify-center"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>

            <div
                ref={scrollRef}
                className={`overflow-x-auto select-none scrollbar-hide ${
                    isDragging ? "cursor-grabbing" : "cursor-grab"
                }`}
                onMouseDown={onMouseDown}
                onMouseLeave={endDrag}
                onMouseUp={endDrag}
                onMouseMove={onMouseMove}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={endDrag}
                onScroll={onScroll}
                onClickCapture={onClickCapture}
                onDragStart={(e) => e.preventDefault()}
                data-testid="montures-scroll"
            >
                <div className="flex gap-4 md:gap-5 px-5 md:px-10 py-2 pb-6">
                    {repeated.map((m, i) => (
                        <div
                            key={`${m.id}-${i}`}
                            data-monture-card
                            className="shrink-0 w-[260px] md:w-[300px]"
                        >
                            <MontureCard
                                monture={m}
                                onClick={() => onSelect && onSelect(m)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
